import React, { useMemo } from 'react';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import CertificationRiskBadge, {
	getCertificationValues,
} from '../../StudentRisk/CertificationRiskBadge';
import Table from './Table';
import { useNavigate } from 'react-router-dom';
import * as ROUTES from '../../../constants/routes';
import { sortByDate } from '../../../utils/date';
import { compareDates } from '../../../utils/sort';
import moment from '../../../moment';

enum RISK {
	HIGH = 3,
	AVERAGE = 2,
	LOW = 1,
}

export type CertificateListEntry = {
	studentId: number;
	studentName: string;
	trainingName: string;
	certificateExpirationDate: string;
	sortDate: string;
	establishmentId: number;
	risk: RISK;
};

type Props = {
	certificates: CertificateListEntry[];
};
const columnHelper = createColumnHelper<CertificateListEntry>();
const CertificatesOverview = ({ certificates }: Props) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const columns = useMemo(
		() =>
			[
				columnHelper.accessor('studentName', {
					header: t('certificates.table.name'),
				}),
				columnHelper.accessor('trainingName', {
					header: t('certificates.table.certificate'),
				}),
				columnHelper.accessor('certificateExpirationDate', {
					header: t('certificates.table.expiryDate'),
					sortingFn: (rowA, rowB) =>
						compareDates(moment(rowA.original.sortDate), moment(rowB.original.sortDate)),
				}),
				columnHelper.accessor('risk', {
					header: t('certificates.table.certificateRisk'),
					cell: (cellInfo) => {
						if (cellInfo.getValue()) {
							return <CertificationRiskBadge risk={cellInfo.getValue()} />;
						}
						return null;
					},
				}),
			] as Array<AccessorKeyColumnDef<CertificateListEntry, unknown>>,
		[t],
	);

	const handleRowClick = (row: CertificateListEntry) => {
		navigate(ROUTES.employeeDetailPage(row.studentId));
	};

	const prepareXlSX = () => {
		const sortedCertificates = certificates.sort((a, b) =>
			sortByDate(a.sortDate, b.sortDate, { order: 'asc' }),
		);
		const mappedCertificates = sortedCertificates.map((certificateData) => ({
			[t('certificates.table.name')]: certificateData.studentName,
			[t('certificates.table.certificate')]: certificateData.trainingName,
			[t('certificates.table.expiryDate')]: certificateData.certificateExpirationDate,
			[t('certificates.table.certificateRisk')]: getCertificationValues(certificateData.risk, t)
				?.value,
		}));
		return [
			{
				name: 'Certificaten overzicht',
				rows: mappedCertificates,
			},
		];
	};

	return (
		<Table
			data={certificates}
			columns={columns}
			handleRowClick={handleRowClick}
			prepareDownload={prepareXlSX}
		/>
	);
};

export default CertificatesOverview;
